import {
  createFileRoute,
  useNavigate,
  useParams,
  useSearch,
} from '@tanstack/react-router';
import type { JSX } from 'react';

import { Skeleton } from '@/components/ui/skeleton.js';
import { DashboardDetailView } from '@/features/dashboards/DashboardDetailView.js';
import { ensureDashboardsEnabled } from '@/features/dashboards/guard.js';
import { useLinkedWorkspace } from '@/features/workspace/useLinkedWorkspace.js';
import { PageContainer } from '@/ui/PageContainer.js';

export interface DashboardSearch {
  /** The workspace a copied dashboard link names — see `useLinkedWorkspace`. */
  workspace?: string;
  /** `focus` opens the Assistant and puts the cursor in its composer. */
  assistant?: 'focus';
}

/**
 * One saved dashboard on its own page, opened from its node in the sidebar tree or from a copied link.
 * A link names the dashboard's workspace, which is selected before the dashboard is looked up.
 */
export const Route = createFileRoute('/dashboards/$id')({
  beforeLoad: () => ensureDashboardsEnabled(),
  validateSearch: (raw: Record<string, unknown>): DashboardSearch => ({
    workspace:
      typeof raw.workspace === 'string' && raw.workspace.trim().length > 0
        ? raw.workspace.trim()
        : undefined,
    assistant: raw.assistant === 'focus' ? 'focus' : undefined,
  }),
  component: DashboardPage,
});

function DashboardPage(): JSX.Element {
  const { id } = useParams({ from: '/dashboards/$id' });
  const search: DashboardSearch = useSearch({ from: '/dashboards/$id' });
  const navigate = useNavigate();
  const settlingLinkedWorkspace = useLinkedWorkspace(
    search.workspace,
    () =>
      void navigate({
        to: '/dashboards/$id',
        params: { id },
        search: (previous) => ({ ...previous, workspace: undefined }),
        replace: true,
      }),
  );

  if (settlingLinkedWorkspace) {
    return (
      <PageContainer maxWidth="4xl" className="h-full gap-6">
        <Skeleton className="h-5 w-48" />
        <Skeleton className="h-64 rounded-xl" />
      </PageContainer>
    );
  }
  return (
    <DashboardDetailView
      dashboardId={id}
      focusAssistant={search.assistant === 'focus'}
    />
  );
}
